import React, {useEffect, useRef, useState} from "react";
import {Routers} from './Router';
import {AddToCoupon} from "../AddToCoupon/AddToCoupon";
import {useDispatch, useSelector} from "react-redux";
import {StoreType} from "../../store/spirs/types";
import Fingerprint2 from 'fingerprintjs2';
import {FingerPrintType} from "../../models/api/codegen/code";
import {SET_FINGERPRINT} from "../../store/spirs/actions";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {ButtonBase} from "../elements";
import {UpButton} from '../Line/UpButton';
import {useLocation} from "react-router-dom";

// пока оставляем так, позже перенастроить стили скролла
// Создаем стилизованный контейнер со скроллбаром
const ScrollContainer = styled.main`
    /* Плавная анимация скролла */
    scroll-behavior: smooth;

    /* Скрыть скроллбар на мобильных устройствах */
    @media (max-width: 768px) {
        &::-webkit-scrollbar {
            display: none;
        }

        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    /* Стили для десктопа */
    @media (min-width: 769px) {
        &::-webkit-scrollbar {
            width: 8px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 4px;

            &:hover {
                background: rgba(255, 255, 255, 0.4);
            }

            &:active {
                background: rgba(255, 255, 255, 0.6);
            }
        }
    }

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
`;

const ShowAllStepsButton = styled(ButtonBase)`
    width: 220px;
    background-color: #EACD40;
    font-weight: 500;
    color: #121518;
    border: 1px solid #EACD40;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    font-size: 14px;
    padding: 10px 20px;
    text-transform: uppercase;
` as typeof ButtonBase;

const options = {
    excludes: {
        addBehavior: true,
        audio: true,
        availableScreenResolution: true,
        colorDepth: true,
        enumerateDevices: true,
        fontsFlash: true,
        hasLiedBrowser: true,
        hasLiedLanguages: true,
        hasLiedOs: true,
        hasLiedResolution: true,
        sessionStorage: true,
        timezoneOffset: true,
        userAgent: true,
        webdriver: true,
        webglVendorAndRenderer: true,
    }
};

interface IProps {
    handleClick: any
}

export const Container = React.memo(({handleClick}: IProps) => {
    const location = useLocation();

    // Эффект для поиска и скролла
    useEffect(() => {
        const fixtureId = location.hash.replace('#fixture-', '');

        // Флаг для предотвращения повторной подсветки
        let isProcessing = false;

        const highlightElement = () => {
            if (isProcessing) return false;

            const element = document.getElementById(`fixture-${fixtureId}`);
            if (!element) return false;

            isProcessing = true;

            // Скролл
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            // Добавляем классы
            element.classList.add('highlight-pulse', '!animate-pulse');

            // Удаляем классы через 3 секунды
            setTimeout(() => {
                element.classList.remove('highlight-pulse', '!animate-pulse');
            }, 3000);

            return true;
        };

        // Пробуем сразу
        if (highlightElement()) return;

        // Только один интервал поиска
        const searchInterval = setInterval(() => {
            if (highlightElement()) {
                clearInterval(searchInterval);
                clearTimeout(failTimeout);
            }
        }, 300);

        // Таймаут на 5 секунд
        const failTimeout = setTimeout(() => {
            console.warn('Элемент не найден за 5 секунд');
            clearInterval(searchInterval);
            isProcessing = false;
        }, 5000);

        return () => {
            clearInterval(searchInterval);
            clearTimeout(failTimeout);
        };
    }, [location.hash]);

    const anchorElId = useSelector((state: StoreType) => state.appSettings.needConfirmDialog.anchorElId);
    const playerToken = useSelector((state: StoreType) => state.spirs.playerToken);
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const isOpenModal = useSelector(
        (state: StoreType) => state.mobileCouponModal
    );

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const savedScrollTop = useRef(0);
    const trackScroll = useRef(false);

    React.useEffect(() => {
        if (playerToken === "GUEST") {
            return;
        }
        setTimeout(() => {
            Fingerprint2.get(options, components => {
                const c = components.reduce((obj: FingerPrintType, item) => {
                    obj[item.key] = item.value;
                    return obj;
                }, {} as FingerPrintType);
                dispatch(SET_FINGERPRINT(c));
            })
        }, 500);
    }, [playerToken]);

    React.useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            const parent = scrollContainer.offsetParent;
            if (parent) {
                const handleScroll = () => {
                    if (trackScroll.current) {
                        savedScrollTop.current = window.scrollY;
                    }
                };

                window.addEventListener("scroll", handleScroll);
                return () => {
                    window.removeEventListener("scroll", handleScroll);
                };
            }
        }
    }, []);

    React.useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            const parent = scrollContainer.offsetParent;
            if (parent && !isOpenModal) {
                window.scrollTo(0, savedScrollTop.current);
            }
        }
        trackScroll.current = !isOpenModal;
    }, [isOpenModal]);

    return (
        <ScrollContainer id="id-scroll" ref={scrollContainerRef}
                         className={`overflow-y-auto flex flex-1 mb-[10px] tablet:mb-[58px] ${isOpenModal ? 'h-0' : ''}`}>
            <AddToCoupon anchorElId={anchorElId}/>
            <div className="py-[16px] tablet:px-[16px] w-full">
                <Content/>
                <div
                    className="max-tablet:hidden tablet:flex notebook:hidden justify-end fixed bottom-0 right-[16px] z-[999]">
                    <ShowAllStepsButton onClick={handleClick}>
                        {t('sideBar.tabs.coupon')}
                    </ShowAllStepsButton>
                </div>
            </div>
            <UpButton/>
        </ScrollContainer>
    );
}, (prevProps, nextProps) => true);

const Content: React.FC = React.memo(() => <Routers/>);
